import React, { useRef, useEffect, useState } from 'react';
import './WoodBreakingChallenge.css';
import { useRoturaImages } from './hooks/useRoturaImages';
import BeltPromotionModal from './BeltPromotionModal';
import PosePreloader from './PosePreloader';
import usePoseLoader from './hooks/usePoseLoader';
import { drawOptimizedSkeleton, calculateGloveSize } from './utils/skeletonUtils';
import useGameScreen from './hooks/useGameScreen';
const PHASES = { WAITING: 'waiting', ARM_UP: 'arm_up', STRIKING: 'striking', GAME_OVER: 'game_over' };
const ZONES = [
  { name: 'weak', color: '#ff6600', min: 0.2, max: 0.35 },
  { name: 'good', color: '#ffcc00', min: 0.35, max: 0.45 },
  { name: 'perfect', color: '#00ff00', min: 0.45, max: 0.55 },
  { name: 'good', color: '#ffcc00', min: 0.55, max: 0.65 },
  { name: 'weak', color: '#ff6600', min: 0.65, max: 0.8 },
  { name: 'miss', color: '#ff0000', min: 0.8, max: 1.0 }
];
const MAX_ROUNDS = 28;
const getMaterialPhase = (round) => {
  if (round <= 7) return 'wood';
  if (round <= 14) return 'fire_wood';
  if (round <= 21) return 'ice';
  return 'concrete';
};
const MATERIAL_LABELS = { wood: 'MADERA', fire_wood: '🔥 MADERA EN LLAMAS', ice: '❄️ HIELO', concrete: '🧱 CONCRETO' };

function WoodBreakingChallenge({ onClose, userData }) {
  useGameScreen();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [phase, setPhase] = useState(PHASES.WAITING);
  const [round, setRound] = useState(1);
  const [totalScore, setTotalScore] = useState(0);
  const [woodCount, setWoodCount] = useState(1);
  const [brokenCount, setBrokenCount] = useState(0);
  const [hitZone, setHitZone] = useState('');
  const [countdown, setCountdown] = useState(3);
  const [isReady, setIsReady] = useState(false);
  const [lives, setLives] = useState(3);
  const [showBeltModal, setShowBeltModal] = useState(false);
  const [beltPromotionData, setBeltPromotionData] = useState(null);
  const [showEnergyWarning, setShowEnergyWarning] = useState(false);
  const [gameSaved, setGameSaved] = useState(false);

  // All game state in refs so the pose callback always sees latest values
  const gameState = useRef({
    phase: PHASES.WAITING,
    round: 1,
    totalScore: 0,
    isReady: false,
    barPos: 0,
    barDir: 1,
    barSpeed: 0.02,
    handWasUp: false,
    brokenCount: 0,
    hitZone: '',
    lives: 3
  });

  const animFrameRef = useRef(null);
  const resultTimer = useRef(null);
  const particles = useRef([]);
  const woodBreakAnim = useRef({ active: false, progress: 0, broken: 0, total: 0 });
  const fireParticles = useRef([]);
  const flashRef = useRef({ active: false, color: '', time: 0 });

  const roturaImages = useRoturaImages();
  const images = useRef({
    gloveHandDerecha: new Image(), gloveHandIzquierda: new Image(),
    gloveFoot: new Image(), gloveFootLeft: new Image(),
    background: new Image(), playerHead: new Image(), playerBody: new Image(),
    wood: new Image(), armImage: new Image(), leftArmImage: new Image(),
    rightLegImage: new Image(), leftLegImage: new Image()
  });
  const breakSoundRef = useRef(null);
  const audioRef = useRef(null);
  const onResultsRef = useRef(null);

  useEffect(() => {
    if (!roturaImages.loading) {
      images.current.gloveHandDerecha.src = roturaImages.gloveHandDerecha;
      images.current.gloveHandIzquierda.src = roturaImages.gloveHandIzquierda;
      images.current.gloveFoot.src = roturaImages.gloveFoot;
      images.current.gloveFootLeft.src = roturaImages.gloveFootLeft;
      images.current.playerHead.src = roturaImages.playerHead;
      images.current.playerBody.src = roturaImages.playerBody;
    }
    images.current.background.src = '/img/backgroundmaderarotura.png';
    images.current.wood.src = '/img/madera.png';
    images.current.armImage.src = '/img/rotura/brazoderecho.png';
    images.current.leftArmImage.src = '/img/rotura/brazoizquierdo.png';
    images.current.rightLegImage.src = '/img/rotura/piederecho.png';
    images.current.leftLegImage.src = '/img/rotura/pieizquierdo.png';
  }, [roturaImages]);

  const getWoodCount = (r) => {
    return ((r - 1) % 7) + 1;
  };
  const getBarSpeed = (r) => 0.015 + r * 0.004;

  const createParticles = (x, y, count) => {
    const colors = ['#8B4513', '#A0522D', '#D2691E', '#CD853F', '#DEB887'];
    for (let i = 0; i < count; i++) {
      const angle = (Math.PI * 2 * i) / count;
      const speed = 4 + Math.random() * 6;
      particles.current.push({
        x, y, vx: Math.cos(angle) * speed, vy: Math.sin(angle) * speed - 3,
        life: 1, color: colors[Math.floor(Math.random() * colors.length)],
        size: 4 + Math.random() * 8
      });
    }
  };

  const evaluateHit = (pos) => {
    for (const zone of ZONES) {
      if (pos >= zone.min && pos < zone.max) return zone.name;
    }
    return 'miss';
  };

  const handleStrike = () => {
    const gs = gameState.current;
    if (gs.phase !== PHASES.ARM_UP) return;

    if (breakSoundRef.current) {
      breakSoundRef.current.currentTime = 0;
      breakSoundRef.current.play().catch(() => {});
    }

    gs.phase = PHASES.STRIKING;
    setPhase(PHASES.STRIKING);

    const zone = evaluateHit(gs.barPos);
    const wCount = getWoodCount(gs.round);
    let broken = 0, points = 0;

    const material = getMaterialPhase(gs.round);
    const materialBonus = material === 'fire_wood' ? 1.5 : material === 'ice' ? 1.8 : material === 'concrete' ? 2.5 : 1;

    if (zone === 'perfect') { broken = wCount; points = Math.round(wCount * 15 * materialBonus); }
    else if (zone === 'good') { broken = Math.ceil(wCount * 0.7); points = Math.round(broken * 10 * materialBonus); }
    else if (zone === 'weak') { broken = Math.ceil(wCount * 0.3); points = Math.round(broken * 5 * materialBonus); }

    // Solo miss quita vida
    if (zone === 'miss') {
      gs.lives -= 1;
      setLives(gs.lives);
      flashRef.current = { active: true, color: 'rgba(255,0,0,0.4)', time: Date.now() };
    }

    gs.brokenCount = broken;
    gs.hitZone = zone;
    gs.totalScore += points;
    setBrokenCount(broken);
    setHitZone(zone);
    setTotalScore(gs.totalScore);
    console.log('Strike ronda', gs.round, '/', MAX_ROUNDS, 'zona:', zone, 'score:', gs.totalScore);

    woodBreakAnim.current = { active: true, progress: 0, broken, total: wCount };

    const currentRound = gs.round;
    const currentLives = gs.lives;
    resultTimer.current = setTimeout(() => {
      woodBreakAnim.current.active = false;
      if (currentLives <= 0 || currentRound >= MAX_ROUNDS) {
        gs.phase = PHASES.GAME_OVER;
        setPhase(PHASES.GAME_OVER);
        handleGameEnd();
      } else {
        gs.round += 1;
        gs.barPos = 0;
        gs.barDir = 1;
        gs.barSpeed = getBarSpeed(gs.round);
        gs.handWasUp = false;
        gs.phase = PHASES.WAITING;
        gs.brokenCount = 0;
        gs.hitZone = '';
        setRound(gs.round);
        setWoodCount(getWoodCount(gs.round));
        setPhase(PHASES.WAITING);
        setBrokenCount(0);
        setHitZone('');
      }
    }, 2500);
  };

  const animateBar = () => {
    const gs = gameState.current;
    if (gs.phase === PHASES.ARM_UP) {
      gs.barPos += gs.barDir * gs.barSpeed;
      if (gs.barPos >= 1) { gs.barPos = 1; gs.barDir = -1; }
      if (gs.barPos <= 0) { gs.barPos = 0; gs.barDir = 1; }
    }
    animFrameRef.current = requestAnimationFrame(animateBar);
  };

  const drawRoundedRect = (ctx, x, y, w, h, r) => {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + r);
    ctx.lineTo(x + w, y + h - r);
    ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    ctx.lineTo(x + r, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
  };

  const drawPowerBar = (ctx, w) => {
    const gs = gameState.current;
    const barW = w * 0.6;
    const barH = 40;
    const barX = (w - barW) / 2;
    const barY = 80;

    ctx.fillStyle = 'rgba(0,0,0,0.7)';
    ctx.strokeStyle = '#fff';
    ctx.lineWidth = 3;
    drawRoundedRect(ctx, barX - 5, barY - 5, barW + 10, barH + 10, 10);
    ctx.fill();
    ctx.stroke();

    ZONES.forEach(zone => {
      ctx.fillStyle = zone.color;
      ctx.globalAlpha = 0.6;
      ctx.fillRect(barX + barW * zone.min, barY, barW * (zone.max - zone.min), barH);
    });
    ctx.globalAlpha = 1;

    ctx.save();
    ctx.translate(barX + barW * 0.5, barY - 10);
    ctx.scale(-1, 1);
    ctx.fillStyle = '#fff';
    ctx.font = 'bold 14px Arial';
    ctx.textAlign = 'center';
    ctx.fillText('PERFECT', 0, 0);
    ctx.restore();

    const indicatorX = barX + barW * gs.barPos;
    ctx.fillStyle = '#fff';
    ctx.shadowColor = '#fff';
    ctx.shadowBlur = 15;
    ctx.beginPath();
    ctx.moveTo(indicatorX, barY - 5);
    ctx.lineTo(indicatorX - 8, barY - 18);
    ctx.lineTo(indicatorX + 8, barY - 18);
    ctx.closePath();
    ctx.fill();
    ctx.fillRect(indicatorX - 3, barY, 6, barH);
    ctx.shadowBlur = 0;
  };

  // Generar partículas de fuego para fase fire_wood
  const updateFireParticles = (stackX, stackBaseY, count, woodH) => {
    const topY = stackBaseY - (count - 1) * (woodH + 4) - woodH;
    for (let i = 0; i < 3; i++) {
      fireParticles.current.push({
        x: stackX + (Math.random() - 0.5) * 160,
        y: topY + Math.random() * 10,
        vx: (Math.random() - 0.5) * 2,
        vy: -(1.5 + Math.random() * 3),
        life: 1,
        size: 4 + Math.random() * 8,
        color: Math.random() > 0.3 ? '#FF6600' : (Math.random() > 0.5 ? '#FF0000' : '#FFCC00')
      });
    }
    fireParticles.current = fireParticles.current.filter(p => {
      p.x += p.vx; p.y += p.vy; p.life -= 0.03; p.size *= 0.96;
      return p.life > 0;
    });
  };

  const drawFireEffect = (ctx) => {
    fireParticles.current.forEach(p => {
      ctx.globalAlpha = p.life * 0.8;
      ctx.shadowBlur = 12;
      ctx.shadowColor = p.color;
      ctx.fillStyle = p.color;
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
      ctx.fill();
    });
    ctx.shadowBlur = 0;
    ctx.globalAlpha = 1;
  };

  const drawConcreteBrick = (ctx, x, y, brickW, brickH, isBroken) => {
    const grad = ctx.createLinearGradient(x, y, x, y + brickH);
    grad.addColorStop(0, isBroken ? '#666' : '#888');
    grad.addColorStop(0.5, isBroken ? '#555' : '#777');
    grad.addColorStop(1, isBroken ? '#444' : '#666');
    ctx.fillStyle = grad;
    ctx.fillRect(x, y, brickW, brickH);
    ctx.strokeStyle = '#333';
    ctx.lineWidth = 2;
    ctx.strokeRect(x, y, brickW, brickH);
    ctx.strokeStyle = 'rgba(0,0,0,0.3)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(x + brickW * 0.3, y);
    ctx.lineTo(x + brickW * 0.35, y + brickH * 0.5);
    ctx.moveTo(x + brickW * 0.7, y + brickH);
    ctx.lineTo(x + brickW * 0.65, y + brickH * 0.4);
    ctx.stroke();
  };

  const drawIceBrick = (ctx, x, y, brickW, brickH, isBroken) => {
    const grad = ctx.createLinearGradient(x, y, x, y + brickH);
    grad.addColorStop(0, isBroken ? '#4488aa' : '#88ccee');
    grad.addColorStop(0.5, isBroken ? '#3377aa' : '#66bbdd');
    grad.addColorStop(1, isBroken ? '#2266aa' : '#44aacc');
    ctx.fillStyle = grad;
    ctx.globalAlpha = 0.85;
    ctx.fillRect(x, y, brickW, brickH);
    ctx.globalAlpha = 1;
    ctx.strokeStyle = '#aaddff';
    ctx.lineWidth = 2;
    ctx.strokeRect(x, y, brickW, brickH);
    ctx.fillStyle = 'rgba(255,255,255,0.3)';
    ctx.fillRect(x + 5, y + 3, brickW * 0.3, 4);
    ctx.fillRect(x + brickW * 0.5, y + brickH - 7, brickW * 0.25, 3);
  };

  const drawWoodStack = (ctx, w, h) => {
    const gs = gameState.current;
    const anim = woodBreakAnim.current;
    const count = anim.active ? anim.total : getWoodCount(gs.round);
    const material = getMaterialPhase(gs.round);
    const woodW = 180, woodH = 25;
    const stackX = w * 0.5;
    const stackBaseY = h * 0.65;

    // Soportes
    const supportColor = material === 'concrete' ? '#444' : material === 'ice' ? '#6699bb' : '#5a3a1a';
    ctx.fillStyle = supportColor;
    ctx.fillRect(stackX - woodW / 2 - 30, stackBaseY + 10, 25, 80);
    ctx.fillRect(stackX + woodW / 2 + 5, stackBaseY + 10, 25, 80);

    // Fuego arriba en fase fire_wood
    if (material === 'fire_wood' && !anim.active) {
      updateFireParticles(stackX, stackBaseY, count, woodH);
      drawFireEffect(ctx);
    }

    for (let i = 0; i < count; i++) {
      const y = stackBaseY - i * (woodH + 4);
      const isBroken = anim.active && i >= (count - anim.broken);

      ctx.save();
      if (isBroken) {
        const t = Math.min(anim.progress / 30, 1);
        ctx.translate(stackX + (i % 2 === 0 ? 1 : -1) * t * 60, y + t * 30);
        ctx.rotate((i % 2 === 0 ? 1 : -1) * t * 0.3);
        ctx.globalAlpha = 1 - t * 0.5;
      } else {
        ctx.translate(stackX, y);
      }

      if (material === 'concrete') {
        drawConcreteBrick(ctx, -woodW / 2, -woodH / 2, woodW, woodH, isBroken);
      } else if (material === 'ice') {
        drawIceBrick(ctx, -woodW / 2, -woodH / 2, woodW, woodH, isBroken);
      } else if (images.current.wood.complete && images.current.wood.naturalWidth > 0) {
        ctx.drawImage(images.current.wood, -woodW / 2, -woodH / 2, woodW, woodH);
        // Tinte naranja para fire_wood
        if (material === 'fire_wood') {
          ctx.fillStyle = 'rgba(255,100,0,0.2)';
          ctx.fillRect(-woodW / 2, -woodH / 2, woodW, woodH);
        }
      } else {
        ctx.fillStyle = material === 'concrete' ? '#777' : (isBroken ? '#654321' : '#8B4513');
        ctx.fillRect(-woodW / 2, -woodH / 2, woodW, woodH);
        ctx.strokeStyle = material === 'concrete' ? '#333' : '#5a3a1a';
        ctx.lineWidth = 2;
        ctx.strokeRect(-woodW / 2, -woodH / 2, woodW, woodH);
      }

      if (isBroken) {
        ctx.strokeStyle = material === 'concrete' ? '#222' : '#000';
        ctx.lineWidth = material === 'concrete' ? 3 : 2;
        ctx.beginPath();
        ctx.moveTo(-woodW / 2, 0);
        ctx.lineTo(0, -5 + Math.random() * 10);
        ctx.lineTo(woodW / 2, 0);
        ctx.stroke();
      }
      ctx.restore();
    }

    // Label del material
    ctx.save();
    ctx.translate(stackX, stackBaseY + 110);
    ctx.scale(-1, 1);
    ctx.fillStyle = material === 'concrete' ? '#aaa' : material === 'ice' ? '#88ddff' : (material === 'fire_wood' ? '#ff6600' : '#ffd700');
    ctx.font = 'bold 22px Arial';
    ctx.textAlign = 'center';
    ctx.fillText(count + ' ' + (material === 'concrete' ? 'LADRILLOS' : material === 'ice' ? 'BLOQUES' : 'TABLAS'), 0, 0);
    ctx.font = 'bold 16px Arial';
    ctx.fillText(MATERIAL_LABELS[material], 0, 25);
    ctx.restore();
  };

  const drawMirroredText = (ctx, text, x, y, font, color) => {
    ctx.save();
    ctx.translate(x, y);
    ctx.scale(-1, 1);
    ctx.fillStyle = color;
    ctx.font = font;
    ctx.textAlign = 'center';
    ctx.fillText(text, 0, 0);
    ctx.restore();
  };

  const drawInstructions = (ctx, w, h) => {
    const gs = gameState.current;
    const boxX = w * 0.3;
    const boxY = h * 0.4;
    const boxW = w * 0.4;
    if (gs.phase === PHASES.WAITING) {
      ctx.fillStyle = 'rgba(0,0,0,0.6)';
      drawRoundedRect(ctx, boxX, boxY, boxW, 80, 15);
      ctx.fill();
      drawMirroredText(ctx, 'LEVANTA LA MANO', w * 0.5, boxY + 35, 'bold 22px Arial', '#ffd700');
      drawMirroredText(ctx, 'Sube la mano derecha arriba de tu cabeza', w * 0.5, boxY + 60, '16px Arial', '#fff');
    } else if (gs.phase === PHASES.ARM_UP) {
      ctx.fillStyle = 'rgba(0,0,0,0.6)';
      drawRoundedRect(ctx, boxX, boxY, boxW, 80, 15);
      ctx.fill();
      drawMirroredText(ctx, 'BAJA CON FUERZA!', w * 0.5, boxY + 35, 'bold 22px Arial', '#00ff00');
      drawMirroredText(ctx, 'Cuando el indicador este en VERDE', w * 0.5, boxY + 60, '16px Arial', '#fff');
    }
  };

  const drawResultText = (ctx, w, h) => {
    const gs = gameState.current;
    if (gs.phase !== PHASES.STRIKING) return;
    const texts = {
      perfect: { text: 'PERFECTO!!', color: '#00ff00', size: 52 },
      good: { text: 'BIEN!', color: '#ffcc00', size: 44 },
      weak: { text: 'DEBIL...', color: '#ff6600', size: 40 },
      miss: { text: 'FALLASTE!', color: '#ff0000', size: 44 }
    };
    const t = texts[gs.hitZone] || texts.miss;
    ctx.save();
    ctx.translate(w * 0.5, h * 0.35);
    ctx.scale(-1, 1);
    ctx.fillStyle = t.color;
    ctx.font = 'bold ' + t.size + 'px Arial';
    ctx.textAlign = 'center';
    ctx.shadowColor = t.color;
    ctx.shadowBlur = 20;
    ctx.fillText(t.text, 0, 0);
    ctx.font = 'bold 28px Arial';
    ctx.fillStyle = '#fff';
    ctx.fillText(gs.brokenCount + ' / ' + getWoodCount(gs.round) + ' tablas rotas', 0, 45);
    ctx.shadowBlur = 0;
    ctx.restore();
  };

  const onResults = (results) => {
    const canvas = canvasRef.current;
    const video = videoRef.current;
    if (!canvas || !video || video.videoWidth === 0) return;
    const ctx = canvas.getContext('2d');
    const gs = gameState.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    ctx.save();
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    if (images.current.background.complete) {
      ctx.save();
      ctx.scale(-1, 1);
      ctx.drawImage(images.current.background, -canvas.width, 0, canvas.width, canvas.height);
      ctx.restore();
    }

    drawWoodStack(ctx, canvas.width, canvas.height);

    if (results.poseLandmarks) {
      const lm = results.poseLandmarks;
      const shoulderDist = drawOptimizedSkeleton(ctx, lm, canvas.width, canvas.height, images.current);

      if (shoulderDist) {
        const gloveSize = calculateGloveSize(shoulderDist);
        [
          { img: images.current.gloveHandDerecha, l: lm[20] },
          { img: images.current.gloveHandIzquierda, l: lm[19] },
          { img: images.current.gloveFootLeft, l: lm[32] },
          { img: images.current.gloveFoot, l: lm[31] }
        ].forEach(({ img, l }) => {
          if (l?.visibility > 0.5 && img?.complete && img.naturalWidth > 0) {
            ctx.globalAlpha = 0.9;
            ctx.drawImage(img, canvas.width * l.x - gloveSize / 2, canvas.height * l.y - gloveSize / 2, gloveSize, gloveSize);
            ctx.globalAlpha = 1;
          }
        });
      }

      // Detect arm raise / strike
      if (gs.isReady && gs.phase !== PHASES.GAME_OVER && gs.phase !== PHASES.STRIKING) {
        const rightWrist = lm[16];
        const nose = lm[0];
        const rightShoulder = lm[12];

        if (rightWrist && nose && rightShoulder &&
            rightWrist.visibility > 0.5 && nose.visibility > 0.5) {
          const handAboveHead = rightWrist.y < nose.y - 0.15;
          const handBelowShoulder = rightWrist.y > rightShoulder.y + 0.05;

          if (gs.phase === PHASES.WAITING && handAboveHead) {
            gs.handWasUp = true;
            gs.phase = PHASES.ARM_UP;
            setPhase(PHASES.ARM_UP);
          } else if (gs.phase === PHASES.ARM_UP && gs.handWasUp && handBelowShoulder) {
            handleStrike();
            createParticles(canvas.width * 0.5, canvas.height * 0.55, 40);
          }
        }
      }
    }

    if (gs.phase === PHASES.ARM_UP) {
      drawPowerBar(ctx, canvas.width);
    }

    drawInstructions(ctx, canvas.width, canvas.height);
    drawResultText(ctx, canvas.width, canvas.height);

    if (woodBreakAnim.current.active) {
      woodBreakAnim.current.progress++;
    }

    // Particles
    particles.current = particles.current.filter(p => {
      p.x += p.vx; p.y += p.vy; p.vy += 0.4; p.life -= 0.02; p.size *= 0.97;
      return p.life > 0;
    });
    particles.current.forEach(p => {
      ctx.globalAlpha = p.life;
      ctx.fillStyle = p.color;
      ctx.fillRect(p.x - p.size / 2, p.y - p.size / 2, p.size, p.size);
    });
    ctx.globalAlpha = 1;

    // Flash de daño
    const now = Date.now();
    if (flashRef.current.active) {
      const elapsed = now - flashRef.current.time;
      if (elapsed < 300) {
        ctx.fillStyle = flashRef.current.color;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      } else {
        flashRef.current.active = false;
      }
    }

    ctx.restore();
  };

  onResultsRef.current = onResults;
  const poseLoader = usePoseLoader((...args) => onResultsRef.current?.(...args));

  useEffect(() => {
    const checkEnergy = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${process.env.REACT_APP_API_URL}/training/start`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ energyCost })
        });
        if (!response.ok) {
          setShowEnergyWarning(true);
          setTimeout(onClose, 3000);
          return;
        }
      } catch (error) {
        console.error('Error al verificar energia:', error);
      }
    };
    checkEnergy();
    if (videoRef.current) poseLoader.start(videoRef.current);
    animFrameRef.current = requestAnimationFrame(animateBar);
    return () => {
      poseLoader.stop();
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
      if (resultTimer.current) clearTimeout(resultTimer.current);
      if (audioRef.current) { audioRef.current.pause(); audioRef.current.currentTime = 0; }
    };
  }, []);

  const handleGameEnd = async () => {
    if (gameSaved) return;
    setGameSaved(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/training/finish`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ score: gameState.current.totalScore, tiempo: 60, precision: 0, modo: 'rotura' })
      });
      if (response.ok) {
        const data = await response.json();
        if (data.subiDeNivel && data.beltData) {
          setBeltPromotionData({ cinturon: data.cinturon, beltData: data.beltData });
          setTimeout(() => setShowBeltModal(true), 2500);
        }
      }
    } catch (error) {
      console.error('Error al guardar desafío:', error);
    }
  };

  // Cuando Pose termina de cargar, iniciar countdown
  useEffect(() => {
    if (!poseLoader.loaded) return;
    if (audioRef.current) {
      audioRef.current.volume = 0.3;
      audioRef.current.play().catch(() => {});
    }
    const countdownTimer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(countdownTimer);
          gameState.current.isReady = true;
          setIsReady(true);
          gameState.current.barSpeed = getBarSpeed(1);
          setWoodCount(getWoodCount(1));
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(countdownTimer);
  }, [poseLoader.loaded]);



  return (
    <div className="wood-challenge-overlay">
      <div className="wood-challenge-container">
        <audio ref={audioRef} src="/audio/maderamusica.mp3" loop />
        <audio ref={breakSoundRef} src="/audio/roturamadera.mp3" />
        <video ref={videoRef} style={{ display: 'none' }} />
        <canvas ref={canvasRef} className="challenge-canvas" />
        {!poseLoader.loaded && (
          <PosePreloader progress={poseLoader.progress} statusText={poseLoader.statusText} title="GOLPE PERFECTO" hint="Dejá el celular en un lugar fijo sin que se caiga. Alejate hasta que te tome el cuerpo completo. Levantá la mano derecha arriba de tu cabeza, cuando aparezca la barra bajá con fuerza en la zona verde." />
        )}
        <button className="challenge-close-btn" onClick={onClose}>✕</button>

        <div className="challenge-header">
          <div className="challenge-stat">{'❤️'.repeat(Math.max(0, lives))}{'🖤'.repeat(Math.max(0, 3 - lives))}</div>
          <div className="challenge-stat">🥋 Ronda {round}/{MAX_ROUNDS}</div>
          <div className="challenge-stat">🎯 {totalScore}</div>
        </div>

        {!isReady && countdown > 0 && (
          <div className="challenge-countdown-overlay">
            <div className="challenge-countdown-number">{countdown}</div>
          </div>
        )}

        {showEnergyWarning && (
          <div className="challenge-game-over">
            <h3>ENERGIA INSUFICIENTE</h3>
            <p>Necesitas {energyCost} de energia para jugar</p>
          </div>
        )}

        {phase === PHASES.GAME_OVER && !showBeltModal && !beltPromotionData && (
          <div className="challenge-game-over">
            <h3>{lives <= 0 ? '¡GAME OVER!' : 'DESAFIO COMPLETADO!'}</h3>
            <h1>{totalScore}</h1>
            <p>{lives <= 0 ? 'Te quedaste sin vidas' : `puntos totales en ${MAX_ROUNDS} rondas`}</p>
            <button className="challenge-menu-btn" onClick={onClose}>VOLVER</button>
          </div>
        )}
        {showBeltModal && beltPromotionData && (
          <BeltPromotionModal
            cinturon={beltPromotionData.cinturon}
            beltData={beltPromotionData.beltData}
            onClose={() => {
              setShowBeltModal(false);
              setBeltPromotionData(null);
              onClose();
            }}
          />
        )}
      </div>
    </div>
  );
}

export default WoodBreakingChallenge;
